/*
  Product detail page behaviour: the image gallery, variant switching, and the
  personalization inputs.

  Variant switching resolves against variant data embedded in the page rather
  than re-rendering the section over the network, so changing a metal or a
  length repaints instantly and costs no request.
*/

/* ── Gallery ────────────────────────────────────────────────────────────── */

class DvGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-dv-gallery-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.controls = Array.from(this.querySelectorAll('[data-dv-gallery-go]'));
    this.active = 0;

    this.controls.forEach((control) => {
      control.addEventListener('click', () => this.goTo(Number(control.dataset.dvGalleryGo)));
    });

    this.track.addEventListener('scroll', () => this.onScroll(), {passive: true});
    this.enableDrag();

    const initial = document.querySelector('[data-dv-gallery-initial]');
    if (initial) this.goToImage(JSON.parse(initial.textContent), false);
  }

  goTo(index, smooth = true) {
    this.track.scrollTo({left: this.track.clientWidth * index, behavior: smooth ? 'smooth' : 'auto'});
    this.setActive(index);
  }

  /** Brings the slide for a given Shopify image id into view. */
  goToImage(imageId, smooth = true) {
    if (imageId == null) return;
    const index = this.slides.findIndex((slide) => slide.dataset.dvImageId === String(imageId));
    if (index >= 0) this.goTo(index, smooth);
  }

  onScroll() {
    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (index !== this.active) this.setActive(index);
  }

  setActive(index) {
    this.active = index;
    this.controls.forEach((control) => {
      const on = Number(control.dataset.dvGalleryGo) === index;
      if (on) control.setAttribute('aria-current', 'true');
      else control.removeAttribute('aria-current');
    });
  }

  enableDrag() {
    let dragging = false;
    let originX = 0;
    let originLeft = 0;

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      dragging = true;
      originX = event.clientX;
      originLeft = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      this.track.scrollLeft = originLeft - (event.clientX - originX);
    });

    const release = () => {
      if (!dragging) return;
      dragging = false;
      this.track.classList.remove('is-dragging');
      // Land on the nearest slide rather than between two.
      this.goTo(Math.round(this.track.scrollLeft / this.track.clientWidth));
    };

    this.track.addEventListener('pointerup', release);
    this.track.addEventListener('pointercancel', release);
    this.track.addEventListener('pointerleave', release);
  }
}

/* ── Personalization ────────────────────────────────────────────────────── */

/*
  Which custom line-item-property inputs a product shows is decided by its
  variant option names, its tags, a couple of title special-cases, and the
  `custom_fields.limit` metafield. Because several rules read the *selected*
  option values, the field list is recomputed on every variant change.
*/

const BUBBLE_LETTER_TITLES = [
  '14K Diamond Bubble Letter Necklace',
  '14K Line Diamond Bubble Letter Necklace',
];
const HEART_ANGEL_TITLE = 'Heart of an Angel 14K Diamond Necklace';

function toInt(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : undefined;
}

function personalizationFields(config, selectedOptions) {
  const fields = [];
  const seen = new Set();
  const add = (field) => {
    if (seen.has(field.property)) return;
    seen.add(field.property);
    fields.push(field);
  };

  const tags = (config.tags || []).map((t) => String(t).toLowerCase());
  const hasOption = (name) =>
    (config.optionNames || []).some((n) => n.toUpperCase() === name.toUpperCase());
  const selected = (name) => {
    const key = Object.keys(selectedOptions).find((k) => k.toUpperCase() === name.toUpperCase());
    return key ? selectedOptions[key] : undefined;
  };
  const limit = toInt(config.limit);

  if (hasOption('YOUR NAME')) {
    const max = toInt(selected('YOUR NAME')) ?? limit ?? 10;
    add({
      key: 'your-name',
      label: 'Your Name',
      property: 'Your Name',
      placeholder: 'Enter Your Name',
      required: true,
      maxLength: max,
      help: `Please enter your name with no spaces (${max} letters allowed)`,
    });
  }

  if (hasOption('# OF LETTERS')) {
    const max = toInt(selected('# OF LETTERS')) ?? limit ?? 10;
    add({
      key: 'your-letters',
      label: 'Your Letters',
      property: 'Your Letters',
      placeholder: 'Enter Your Letters',
      required: true,
      maxLength: max,
      help: 'Please enter your letters only, with no spaces',
    });
  }

  if (hasOption('Name Bar Qty.')) {
    const qty = Math.min(Math.max(toInt(selected('Name Bar Qty.')) ?? 1, 1), 4);
    for (let i = 1; i <= qty; i += 1) {
      add({
        key: `name-bar-${i}`,
        label: `Name Bar ${i}`,
        property: `Name Bar ${i}`,
        placeholder: `Name Bar ${i}`,
        required: true,
        maxLength: 6,
      });
    }
  }

  if (BUBBLE_LETTER_TITLES.includes(config.title) && hasOption('Chain')) {
    add({
      key: 'your-letter',
      label: 'Your Letter',
      property: 'Your Letter',
      placeholder: 'Your Letter',
      required: true,
      maxLength: 1,
      lettersOnly: true,
      help: 'Please enter a single letter (no numbers).',
    });
  }

  if (config.title === HEART_ANGEL_TITLE && hasOption('Size')) {
    add({
      key: 'angel-name',
      label: 'Name',
      property: 'Name',
      placeholder: 'Enter a Name',
      required: true,
      maxLength: 8,
      lettersOnly: true,
      help: 'Please enter a name.',
    });
  }

  // Skipped when a "# OF LETTERS" option already produced a Your Letters field.
  if (tags.includes('showinput') && !seen.has('Your Letters')) {
    const base = toInt(selected('NO. OF LETTERS'));
    const max = base !== undefined ? base + 1 : limit !== undefined ? limit + 1 : 11;
    add({
      key: 'your-letters',
      label: 'Your Letters',
      property: 'Your Letters',
      placeholder: 'Enter Your Letters',
      required: true,
      maxLength: max,
      help: 'Please enter your letters only, with no spaces',
    });
  }

  if (tags.includes('twowords')) {
    add({
      key: 'two-names',
      label: 'Two Names',
      property: 'Two Names',
      placeholder: 'NAME NAME',
      required: true,
      maxLength: 17,
      twoWords: true,
      help: 'First name, a space, then second name (16 letters total).',
    });
  }

  return fields;
}

function sanitizeFieldValue(field, raw) {
  let value = raw;
  if (field.lettersOnly) value = value.replace(/[^A-Za-z]/g, '');
  if (field.twoWords) value = value.split(' ').slice(0, 2).join(' ');
  if (field.maxLength != null) value = value.slice(0, field.maxLength);
  return value;
}

/* ── Product ────────────────────────────────────────────────────────────── */

class DvProduct extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('[data-dv-product-config]');
    if (!configEl) return;
    this.config = JSON.parse(configEl.textContent);

    this.gallery = this.querySelector('dv-gallery');
    this.priceMount = this.querySelector('[data-dv-price]');
    this.variantInput = this.querySelector('[name="id"]');
    this.submit = this.querySelector('[type="submit"]');
    this.submitLabel = this.submit?.querySelector('span');
    this.pzMount = this.querySelector('[data-dv-personalization]');
    this.values = {};
    // Positional option values for the variant currently on screen. Seeded from
    // the server-rendered variant so options that render no chips still count.
    this.currentOptions = (this.config.initialOptions || []).slice();

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-dv-option]')) this.onOptionChange();
    });

    /*
      Capture phase, so this runs before the `product-form` element's own
      submit handler and can stop the add outright.
    */
    this.querySelector('form[data-type="add-to-cart-form"]')?.addEventListener(
      'submit',
      (event) => {
        if (this.validatePersonalization()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true,
    );

    this.renderPersonalization();
  }

  get selectedOptions() {
    const map = {};
    (this.config.optionNames || []).forEach((name, index) => {
      map[name] = this.currentOptions[index];
    });
    return map;
  }

  onOptionChange() {
    const wanted = this.currentOptions.slice();
    this.querySelectorAll('[data-dv-option]:checked').forEach((input) => {
      wanted[Number(input.dataset.optionPosition) - 1] = input.value;
    });
    this.currentOptions = wanted;

    const variant = (this.config.variants || []).find(
      (candidate) =>
        candidate.options.length === wanted.length &&
        candidate.options.every((value, index) => value === wanted[index]),
    );

    this.markSelectedChips();
    if (!variant) {
      this.setUnavailable();
      this.renderPersonalization();
      return;
    }

    this.currentOptions = variant.options.slice();

    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.disabled = !variant.available;
    }
    if (this.priceMount) this.priceMount.innerHTML = variant.price_html;
    if (variant.featured_image_id != null) this.gallery?.goToImage(variant.featured_image_id);

    this.setAvailability(variant.available);
    this.renderPersonalization();

    // Keep the address shareable without reloading the page.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  markSelectedChips() {
    this.querySelectorAll('[data-dv-option]').forEach((input) => {
      const chip = this.querySelector(`label[for="${input.id}"]`);
      chip?.classList.toggle('is-selected', input.checked);
    });
  }

  setAvailability(available) {
    if (!this.submit) return;
    this.submit.disabled = !available;
    if (this.submitLabel) {
      this.submitLabel.textContent = available ? this.config.addLabel : this.config.soldOutLabel;
    }
  }

  setUnavailable() {
    if (this.variantInput) this.variantInput.disabled = true;
    this.setAvailability(false);
    if (this.submitLabel) this.submitLabel.textContent = this.config.unavailableLabel;
  }

  renderPersonalization() {
    if (!this.pzMount) return;
    const fields = personalizationFields(this.config.personalization || {}, this.selectedOptions);

    if (!fields.length) {
      this.pzMount.innerHTML = '';
      return;
    }

    this.pzMount.innerHTML = fields
      .map((field) => {
        const value = sanitizeFieldValue(field, this.values[field.key] || '');
        this.values[field.key] = value;
        return `
          <div class="dv-pz__field">
            <label class="dv-pz__label" for="dv-pz-${field.key}">${field.label}</label>
            <input
              class="dv-pz__input"
              id="dv-pz-${field.key}"
              type="text"
              name="properties[${field.property}]"
              placeholder="${field.placeholder}"
              maxlength="${field.maxLength}"
              value="${value.replace(/"/g, '&quot;')}"
              ${field.required ? 'required' : ''}
            >
            ${field.help ? `<p class="dv-pz__help">${field.help}</p>` : ''}
          </div>`;
      })
      .join('');

    this.pzFields = fields;
    this.pzMount.querySelectorAll('.dv-pz__input').forEach((input, index) => {
      const field = fields[index];
      input.addEventListener('input', () => {
        const cleaned = sanitizeFieldValue(field, input.value);
        if (cleaned !== input.value) input.value = cleaned;
        this.values[field.key] = cleaned;
        // Clear the error as soon as they start filling it in.
        if (cleaned.trim()) this.setFieldError(input, null);
      });
    });
  }

  /*
    The buy form carries `novalidate` (Dawn suppresses native bubbles and
    reports errors itself), so a required personalization field would otherwise
    submit empty and the piece would be engraved with nothing.
  */
  validatePersonalization() {
    if (!this.pzMount) return true;
    let firstInvalid = null;

    this.pzMount.querySelectorAll('.dv-pz__input').forEach((input, index) => {
      const field = this.pzFields?.[index];
      if (!field?.required) return;
      const empty = !input.value.trim();
      this.setFieldError(input, empty ? `${field.label} ${this.config.requiredSuffix}` : null);
      if (empty && !firstInvalid) firstInvalid = input;
    });

    if (firstInvalid) {
      firstInvalid.focus();
      firstInvalid.scrollIntoView({block: 'center', behavior: 'smooth'});
    }
    return !firstInvalid;
  }

  setFieldError(input, message) {
    const wrapper = input.closest('.dv-pz__field');
    if (!wrapper) return;
    let error = wrapper.querySelector('.dv-pz__error');

    if (!message) {
      error?.remove();
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
      return;
    }

    if (!error) {
      error = document.createElement('p');
      error.className = 'dv-pz__error';
      error.id = `${input.id}-error`;
      error.setAttribute('role', 'alert');
      wrapper.appendChild(error);
    }
    error.textContent = message;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', error.id);
  }
}

if (!customElements.get('dv-gallery')) customElements.define('dv-gallery', DvGallery);
if (!customElements.get('dv-product')) customElements.define('dv-product', DvProduct);
